import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import screenshotImg from "assets/onboarding/bento-ig-screenshot.png";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { OnboardAction } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { INSTAGRAM_USERNAME_URL } from "schemas/social";

import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";
import { useTracking } from "utils/useTracking";

import { isValidUsername, sanitizeInput } from "../AboutForm/helpers";
import styles from "../styles";
import StepChip from "./StepChip";

interface Props {
  handleBack: () => void;
  handleSkip?: () => void;
  handleNext: () => void;
}

const InstagramOnboardingSteps = ({
  handleSkip,
  handleNext,
  handleBack,
}: Props) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentOrg, profile, setProfile } = useContext(
    OrganizationUserContext,
  );
  const { getToken } = useAuth();
  const location = useLocation();

  const { setErrorAlert } = useContext(AlertContext);
  const [instagramUsername, setInstagramUsername] = useState<string>("");
  const [instagramUsernameValidation, setInstagramUsernameValidation] =
    useState<string | undefined>();
  const [connectInstagramDialogOpen, setConnectInstagramDialogOpen] =
    useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [followedBento, setFollowedBento] = useState(false);
  const [clickedMessage, setClickedMessage] = useState(false);

  const { trackUserHasOnboarded } = useTracking();

  const saveInstagram = async () => {
    const data: Map = {};

    trackEvent("Onboarding Form - Save Instagram Pressed", {
      page: "Connect Instagram",
      route: location.pathname,
    });

    if (instagramUsername) {
      data.instagramUrl = `${INSTAGRAM_USERNAME_URL}${instagramUsername}`;
      setProfile((prev) => {
        if (!prev) return prev;
        const copy = makeDeepCopy(prev);
        copy.instagramUrl = data.instagramUrl;
        return copy;
      });
    }
    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings`,
        "PUT",
        {},
        data,
      );
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
      setConnectInstagramDialogOpen(false);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const sanitizedInput = sanitizeInput(value.trim());
    setInstagramUsername(sanitizedInput);

    if (sanitizedInput && !isValidUsername(sanitizedInput)) {
      setInstagramUsernameValidation(
        "Must contain only letters, numbers, underscores, hyphens or periods",
      );
    } else {
      setInstagramUsernameValidation(undefined);
    }
  };

  useEffect(() => {
    if (profile?.instagramUrl && !instagramUsername) {
      setInstagramUsername(sanitizeInput(profile?.instagramUrl));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.instagramUrl]);

  return (
    <>
      <Grid container justifyContent={"center"}>
        <Box
          sx={{
            borderRadius: 20,
            fontWeight: 500,
            border: "1px black solid",
            px: 2,
            py: 1,
            width: "max-content",
            mb: 1,
          }}
        >
          Step 5/5
        </Box>
      </Grid>

      <Typography sx={{ textAlign: "center", fontSize: { xs: 20, md: 40 } }}>
        Let's get your Instagram connected
      </Typography>

      <Typography sx={{ mb: 3, textAlign: "center" }}>
        Connect with Bento on Instagram to curate your brand pitch list with
        your favorite creators, and get pitching tips and support from our team
        anytime!
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Box sx={styles.card}>
            <StepChip title="Step 1" />
            <Typography
              variant="h6"
              sx={{ width: "70%", lineHeight: "1.4rem", mb: 4 }}
            >
              Connect your Instagram account
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                trackEvent("Onboarding Form - Clicked Connect Instagram", {
                  page: "Connect Instagram",
                  route: location.pathname,
                });
                setConnectInstagramDialogOpen(true);
              }}
            >
              Connect Instagram
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={[styles.card, !instagramUsername && styles.disabled]}>
            <StepChip title="Step 2" />
            <Typography
              variant="h6"
              sx={{ width: "80%", lineHeight: "1.4rem", mb: 4 }}
            >
              Send Bento a DM with the keyword <strong>"Bento"</strong>
            </Typography>
            <Button
              variant="outlined"
              disabled={!instagramUsername}
              fullWidth
              onClick={() => {
                trackEvent("Onboarding Form - Clicked Send DM", {
                  page: "Connect Instagram",
                  route: location.pathname,
                });
                window.open(`https://ig.me/m/onbento`, "_blank");
                setClickedMessage(true);
              }}
              sx={{ mb: 2 }}
            >
              Send DM
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setFollowedBento(event.target.checked);
                    trackEvent("Onboarding Form - Clicked Followed Bento", {
                      page: "Connect Instagram",
                      route: location.pathname,
                      checked: event.target.checked,
                    });
                  }}
                  checked={followedBento}
                  disableRipple
                  icon={
                    <Box
                      sx={{ color: "green.main" }}
                      component="i"
                      className="fa-regular fa-square"
                    />
                  }
                  checkedIcon={
                    <Box
                      component="i"
                      className="fa-solid fa-square-check"
                      sx={{ color: "green.main" }}
                    />
                  }
                />
              }
              label={
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  I've followed Bento
                </Typography>
              }
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            sx={[
              styles.card,
              !followedBento && !clickedMessage && styles.disabled,
            ]}
          >
            <Chip
              label={
                <Typography sx={{ fontWeight: 700, fontSize: "13px" }}>
                  Step 3
                </Typography>
              }
              variant="filled"
              sx={{
                alignSelf: "center",
                flexDirection: "row-reverse",
                mb: 2,
                backgroundColor: "#F7EE6F",
              }}
            />
            <Typography
              variant="h6"
              sx={{ width: "80%", lineHeight: "1.4rem", mb: 2 }}
            >
              Needs tips? DM Bento "Help" anytime.
            </Typography>
            <Box
              component="img"
              src={screenshotImg}
              alt="Instagram Notification"
              sx={styles.screenshotImg}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container sx={{ mt: 6 }}>
        <Grid item xs="auto">
          <Button onClick={handleBack} disableElevation>
            <Box
              component="i"
              className="fa-regular fa-circle-arrow-left"
              sx={{ mr: 1 }}
            />
            Back
          </Button>
        </Grid>
        <Grid item xs></Grid>
        {handleSkip && (
          <Grid item xs="auto">
            <Button
              onClick={handleSkip}
              sx={{ color: "purple.main" }}
              disableElevation
            >
              Skip
            </Button>
          </Grid>
        )}
        <Grid item xs="auto">
          <Button
            onClick={() => {
              trackUserHasOnboarded(OnboardAction.FINAL_OPT_IN);
              handleNext();
            }}
            disableElevation
            variant="contained"
            type="submit"
          >
            Next{" "}
            <Box
              component="i"
              className="fa-regular fa-circle-arrow-right"
              sx={{ ml: 1 }}
            />
          </Button>
        </Grid>
      </Grid>

      <Dialog
        open={connectInstagramDialogOpen}
        onClose={() => {
          setConnectInstagramDialogOpen(false);
        }}
        maxWidth="sm"
        fullScreen={isMobileScreen}
        fullWidth
      >
        <DialogTitle>What is your Instagram handle?</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="username"
            value={instagramUsername}
            required
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                >
                  {INSTAGRAM_USERNAME_URL}
                </InputAdornment>
              ),
            }}
            onChange={handleUsernameChange}
            error={!!instagramUsernameValidation}
            helperText={instagramUsernameValidation}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConnectInstagramDialogOpen(false);
            }}
            color="secondary"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={saveInstagram}
            variant="contained"
            loading={loading}
            disabled={!instagramUsername || !!instagramUsernameValidation}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InstagramOnboardingSteps;
